import React, { useContext, useEffect, useMemo } from "react";
import PageMain from "../../../../components/Main/PageMain";
import UserTrainingContext from "../../../../context/userTrainingContext";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../components/Spinner";
import FriendlyInfo from "../../../../components/FriendlyInfo";
import { Box, Typography } from "@mui/material";
import { TrainingGameCard } from "../TrainingGameCard/TrainingGameCard";
import { TrainingRandomGame } from "../TrainingRandomGame/TrainingRandomGame";
import { useGameList } from "./useGamesList";

// Card de renderização dos jogos
export default function GamesList() {
  const tContext = useContext(UserTrainingContext);
  const { game_list, active_category, setGameUrl } = tContext ?? {};
  const { t } = useTranslation();

  // Hook responsável por filtrar os jogos e tratar a seleção
  const {
    activeCategoryAux,
    filteredGameList,
    randomGame,
    handleGameClick,
    normalize,
  } = useGameList({
    active_category,
    setGameUrl,
    game_list,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Formata o texto deixando a primeira letra de cada palavra maiúscula
  const formatLabel = (text?: string) =>
    text?.replace(/(^|\s)./g, (char) => char.toUpperCase());

  // Retorna as habilidades que serão exibidas no card
  const getAbilities = (game: any) => {
    
    const primaryAbility =
      game?.abilities.find(
        (ability: any) => normalize(ability) === normalize(activeCategoryAux),
      ) || activeCategoryAux;

    const secondaryAbility = game?.abilities.find(
      (ability: any) => normalize(ability) !== normalize(activeCategoryAux),
    );

    return [
      formatLabel(primaryAbility),
      formatLabel(secondaryAbility),
    ].filter(Boolean);
  };

  // Memoriza a lista de cards
  const gamesCards = useMemo(() => {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
          gap: { xs: 2, sm: 3 },
          width: "100%",
          maxWidth: "1000px",
          margin: "0 auto",
          justifyContent: "center",
          alignItems: "start",
        }}
      >
        {filteredGameList?.map((game: any) => {
          return (
            <TrainingGameCard
              key={game.id ?? game.name}
              id={game.id}
              name={game.name}
              image={game.images.thumb}
              abilities={getAbilities(game)}
              onClick={() => handleGameClick(game)}
            />
          );
        })}
      </Box>
    );
  }, [handleGameClick, filteredGameList]);

  // Memoriza o card do jogo aleatório
  const randomGameCard = useMemo(() => {
    if (!randomGame) return null;
    return (
      <TrainingRandomGame
        key={randomGame.id ?? randomGame.name}
        id={randomGame.id}
        name={randomGame.name}
        image={randomGame.images.thumb}
        abilities={getAbilities(randomGame)}
        onClick={() => {
          handleGameClick(randomGame);
        }}
      />
    );
  }, [randomGame, handleGameClick]);

  if (!game_list) {
    return <Spinner center />;
  }

  // Caso ocorra erro ou a lista venha vazia
  if (game_list && game_list.length === 0) {
    return (
      <FriendlyInfo
        isError
        text={t("infos.error_to_fetch_info_try_to_reload")}
      />
    );
  }

  return (
    <>
      <PageMain centerStretch>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            width: "100%",
            maxWidth: "1000px",
            margin: "0 auto",
            gap: 2,
          }}
        >
          {randomGameCard}
          <Box>
            <Typography
              sx={{
                mb: 2,
                fontWeight: 600,
                fontSize: "20px",
                color: "#3D3F40",
              }}
            >
              {t("global.all_training_sessions")} {activeCategoryAux}
            </Typography>

            {gamesCards}
          </Box>
        </Box>
      </PageMain>
    </>
  );
}
